import { ChannelType, NotificationPrivacy } from '../types';

/**
 * OS 알림(데스크톱 토스트·웹 시스템 알림)에 실제로 찍힐 제목과 본문을 계산한다.
 *
 * 알림 문구를 만드는 곳은 App.tsx의 message:notify 핸들러 한 곳뿐이고, 이 함수가 그 한 곳이
 * 부르는 순수부다. 두 벌로 두면 "PC에서는 숨겨지는데 웹에서는 보인다" 식으로 갈라진다.
 *
 * 제목은 대화방 이름이다. 그룹 대화방에서는 그 때문에 **본문 앞이 발신자를 알릴 수 있는
 * 유일한 자리**라, 세 단계 모두 본문으로 발신자를 드러낸다('전부 숨김' 제외).
 * 1:1 대화(dm·self)는 제목이 이미 상대 이름이므로 본문에 또 적지 않는다 — 같은 이름이
 * 두 번 나온다.
 *
 * 'sender'의 본문 문구는 서비스워커가 웹푸시에 쓰는 문장(public/service-worker.js)과
 * 글자까지 똑같이 맞춰 둔다 — 같은 계정의 PC와 폰이 같은 말을 해야 한다.
 *
 * ⚠ 이 설정은 OS 알림에만 적용된다. 채널 목록 미리보기 등 앱 안의 표시는 건드리지 않는다 —
 *   화면을 보고 있는 사람에게서 내용을 숨길 이유가 없고, 숨기면 앱을 쓸 수가 없다.
 */

export const DEFAULT_NOTIFICATION_PRIVACY: NotificationPrivacy = 'full';

/** 알 수 없는 값·undefined는 전부 기본값으로 접는다(서버 normalizeNotifyPrivacy와 같은 규칙). */
export const normalizeNotificationPrivacy = (value: unknown): NotificationPrivacy =>
  value === 'sender' || value === 'hidden' || value === 'full'
    ? value
    : DEFAULT_NOTIFICATION_PRIVACY;

/** 내용을 숨길 때 쓰는 본문. 로그아웃 익명 알림·서비스워커 폴백과 같은 문장이다. */
const GENERIC_BODY = '새 메시지가 도착했습니다';

/** 발신자도 대화방도 드러내지 않을 때 쓰는 제목. */
const GENERIC_TITLE = 'INA-TALK';

export interface NotificationTextInput {
  /** 대화방 이름. DM은 서버가 상대방 이름으로 치환해 내려주므로 이것도 발신자 정보다. */
  channelName?: string | null;
  senderName?: string | null;
  /** 메시지 원문. 첨부만 있는 메시지는 빈 문자열로 들어온다. */
  text?: string | null;
  /** 대화방 종류. 1:1(dm·self)인지 가리는 데만 쓴다. */
  channelType?: ChannelType | null;
}

export interface NotificationText {
  title: string;
  body: string;
}

/**
 * 제목이 이미 사람 이름인가.
 *
 * channelType이 오지 않는 구버전 payload는 이름만으로 판정한다 — 대화방 이름이 없으면
 * 제목이 발신자로 접히고(아래 namedTitle), 대화방 이름이 발신자와 같으면 서버가 수신자
 * 기준으로 치환해 준 DM이다.
 */
const titleIsPerson = (
  channelType?: ChannelType | null,
  channelName?: string | null,
  senderName?: string | null,
): boolean =>
  channelType
    ? channelType === 'dm' || channelType === 'self'
    : !channelName || channelName === senderName;

export function buildNotificationText(
  privacy: NotificationPrivacy,
  { channelName, senderName, text, channelType }: NotificationTextInput,
): NotificationText {
  // 대화방 이름이 없으면 발신자 이름이 제목이 된다(기존 동작).
  const namedTitle = channelName || senderName || GENERIC_TITLE;

  if (privacy === 'hidden') {
    return { title: GENERIC_TITLE, body: GENERIC_BODY };
  }

  if (privacy === 'sender') {
    return {
      title: namedTitle,
      body: senderName ? `${senderName}님의 새 메시지` : GENERIC_BODY,
    };
  }

  // full — 첨부만 있는 메시지는 text가 빈 문자열이라 본문이 비어 버린다.
  // 채널 목록 미리보기와 같은 폴백을 쓴다.
  const bodyText = text || '(첨부파일)';
  const showSender = !!senderName && !titleIsPerson(channelType, channelName, senderName);
  return {
    title: namedTitle,
    body: showSender ? `${senderName}: ${bodyText}` : bodyText,
  };
}

/** 시스템 알림 내용을 숨길 때 쓰는 본문. 서비스워커의 시스템 알림 폴백과 같은 문장이다. */
const GENERIC_SYSTEM_BODY = '새 알림이 도착했습니다';

export interface SystemNotificationTextInput {
  /** 출처 라벨('전자결재') — 서버 config/integrations.js */
  label: string;
  /** 알림 제목('[휴가계] 홍길동님의 결재 요청') */
  title?: string | null;
  /** 문서 제목 */
  subject?: string | null;
}

/**
 * 외부 시스템 알림(알림함 type='system')의 OS 알림 문구.
 * 제목은 언제나 출처 라벨이다(서버 웹푸시 payload의 title과 같다 — 'hidden'이어도 라벨은 남긴다).
 *  - full   : 알림 제목 + 문서 제목(데스크톱은 폰보다 한 줄 더 보인다 — 메시지 알림과 같은 비대칭)
 *  - sender : 알림 제목만(폰 웹푸시와 같다)
 *  - hidden : 폴백 문장(폰 웹푸시와 같다)
 * 링크는 어느 단계에서도 싣지 않는다.
 */
export function buildSystemNotificationText(
  privacy: NotificationPrivacy,
  { label, title, subject }: SystemNotificationTextInput,
): NotificationText {
  const heading = label || GENERIC_TITLE;
  if (privacy === 'hidden') return { title: heading, body: GENERIC_SYSTEM_BODY };
  const main = (title || '').trim() || GENERIC_SYSTEM_BODY;
  if (privacy === 'sender') return { title: heading, body: main };
  const sub = (subject || '').trim();
  return { title: heading, body: sub ? `${main}\n제목: ${sub}` : main };
}
